import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { dictionary, locales } from '../data/locales';
import type { Message } from '../types/chat';
interface LocaleValue {
  locale: string;
  setLocale: (code: string) => void;
  t: (key: string) => string;
  currencyCode: string;
  dir: 'ltr' | 'rtl';
  autoTranslate: boolean;
  setAutoTranslate: (on: boolean) => void;
  /** Resolves what to show for a chat message under the current language settings. */
  readMessage: (message: Message) => {
    text: string;
    translatedFrom?: string;
    original?: string;
  };
}
const LocaleContext = createContext<LocaleValue | null>(null);
export function LocaleProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [locale, setLocale] = useState('en-TH');
  const [autoTranslate, setAutoTranslate] = useState(true);
  const def = locales.find(l => l.code === locale) ?? locales[0];
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = def.dir;
    return () => {
      document.documentElement.dir = 'ltr';
    };
  }, [locale, def.dir]);
  const t = useCallback((key: string) => dictionary[locale]?.[key] ?? dictionary['en-TH'][key] ?? key, [locale]);
  const readMessage = useCallback((message: Message) => {
    const target = locale.split('-')[0];
    if (!autoTranslate || message.language.split('-')[0] === target) return {
      text: message.body
    };
    const translations = message.translations ?? {};
    const translated = translations[locale] ?? Object.entries(translations).find(([code]) => code.split('-')[0] === target)?.[1];
    if (!translated) return {
      text: message.body
    };
    const from = locales.find(l => l.code === message.language)?.english ?? message.language;
    return {
      text: translated,
      translatedFrom: from,
      original: message.body
    };
  }, [autoTranslate, locale]);
  const value = useMemo(() => ({
    locale,
    setLocale,
    t,
    currencyCode: def.currency,
    dir: def.dir,
    autoTranslate,
    setAutoTranslate,
    readMessage
  }), [locale, t, def.currency, def.dir, autoTranslate, readMessage]);
  return <LocaleContext.Provider value={value} data-id="element-1001" data-mp="1kcciqwncrpaj">{children}</LocaleContext.Provider>;
}
export function useLocale(): LocaleValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside LocaleProvider');
  return ctx;
}